import { useState } from "react";

export default function PrivacyPolicy() {
  const [lang, setLang] = useState<"es" | "en">("es");

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Public+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap"
      />
      <style>{`
        .lp-root {
          --paper: #f3f4f6;
          --paper-raised: #ffffff;
          --ink: #14171a;
          --ink-soft: #565d66;
          --line: #d9dce1;
          --accent: #b06f2c;
          --accent-soft: #f1e3ce;
          --accent-soft-line: #e3cca3;
          --mono: "IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace;
          --serif: "Fraunces", Georgia, "Iowan Old Style", serif;
          --sans: "Public Sans", -apple-system, "Segoe UI", Roboto, sans-serif;

          background: var(--paper);
          color: var(--ink);
          font-family: var(--sans);
          font-size: 16px;
          line-height: 1.6;
          padding: 0 24px 96px;
          -webkit-font-smoothing: antialiased;
        }

        @media (prefers-color-scheme: dark) {
          .lp-root {
            --paper: #14171a;
            --paper-raised: #1b1f24;
            --ink: #e7e9ec;
            --ink-soft: #9ba3ac;
            --line: #2a2f35;
            --accent: #e0a052;
            --accent-soft: #372a16;
            --accent-soft-line: #4a3a20;
          }
        }

        .lp-page { max-width: 680px; margin: 0 auto; }

        .lp-header {
          display: flex; align-items: center; gap: 16px;
          padding: 48px 0 28px; border-bottom: 1px solid var(--line);
        }

        .lp-mark { flex: none; width: 44px; height: 44px; border-radius: 10px; object-fit: cover; }

        .lp-head-text h1 {
          font-family: var(--serif); font-weight: 500; font-size: 1.5rem;
          letter-spacing: -0.01em; margin: 0 0 4px; text-wrap: balance;
          color: var(--ink-soft);
        }
        .lp-head-meta {
          display: flex; gap: 10px; align-items: center;
          font-family: var(--mono); font-size: 0.72rem; color: var(--ink-soft);
          text-transform: uppercase; letter-spacing: 0.04em;
        }

        .lp-lang-toggle {
          margin-left: auto; display: flex; gap: 2px;
          background: var(--line); border-radius: 999px; padding: 2px;
          flex: none;
        }
        .lp-lang-toggle button {
          border: none; background: transparent; cursor: pointer;
          font-family: var(--mono); font-size: 0.72rem; text-transform: uppercase;
          letter-spacing: 0.04em; padding: 5px 12px; border-radius: 999px;
          color: var(--ink-soft);
        }
        .lp-lang-toggle button.active {
          background: var(--paper-raised); color: var(--accent);
        }

        .lp-summary {
          margin-top: 32px; display: grid; grid-template-columns: repeat(2, 1fr);
          gap: 1px; background: var(--line); border: 1px solid var(--line);
          border-radius: 10px; overflow: hidden;
        }
        .lp-summary-card { background: var(--paper-raised); padding: 16px 18px; }
        .lp-eyebrow {
          font-family: var(--mono); font-size: 0.68rem; text-transform: uppercase;
          letter-spacing: 0.06em; color: var(--ink-soft); margin: 0 0 8px;
        }
        .lp-summary-card ul { margin: 0; padding-left: 18px; font-size: 0.88rem; }
        .lp-summary-card li { margin-bottom: 3px; }
        .lp-summary-card.no ul { color: var(--ink-soft); }

        .lp-toc { margin-top: 32px; font-size: 0.9rem; }
        .lp-toc ol {
          margin: 0; padding-left: 0; list-style: none;
          columns: 2; column-gap: 24px;
        }
        .lp-toc li { break-inside: avoid; margin-bottom: 6px; }
        .lp-toc a { color: var(--ink-soft); text-decoration: none; border-bottom: 1px solid transparent; }
        .lp-toc a:hover, .lp-toc a:focus-visible { color: var(--accent); border-bottom-color: var(--accent-soft-line); }

        .lp-article { margin-top: 40px; }
        .lp-section { padding: 30px 0; border-bottom: 1px solid var(--line); }
        .lp-section:last-of-type { border-bottom: none; }
        .lp-section h2 {
          display: flex; align-items: baseline; gap: 10px;
          font-family: var(--serif); font-weight: 600; font-size: 1.2rem;
          letter-spacing: -0.005em; margin: 0 0 14px; scroll-margin-top: 20px;
          text-wrap: balance;
        }
        .lp-num { font-family: var(--mono); font-size: 0.85rem; font-weight: 400; color: var(--accent); }
        .lp-section p { margin: 0 0 12px; max-width: 62ch; }
        .lp-section ul, .lp-section ol { margin: 0 0 12px; padding-left: 20px; max-width: 62ch; }
        .lp-section ol li { margin-bottom: 6px; }
        .lp-section li { margin-bottom: 6px; }

        .lp-table-wrap { overflow-x: auto; }
        .lp-section table { width: 100%; border-collapse: collapse; margin: 4px 0 12px; font-size: 0.88rem; }
        .lp-section th, .lp-section td { text-align: left; padding: 8px 10px; border-bottom: 1px solid var(--line); vertical-align: top; }
        .lp-section th {
          font-family: var(--mono); font-size: 0.68rem; text-transform: uppercase;
          letter-spacing: 0.05em; color: var(--ink-soft); font-weight: 500;
        }

        .lp-footer {
          margin-top: 40px; padding-top: 24px; border-top: 1px solid var(--line);
          display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap;
          font-family: var(--mono); font-size: 0.72rem; color: var(--ink-soft);
          text-transform: uppercase; letter-spacing: 0.04em;
        }

        @media (max-width: 520px) {
          .lp-header { flex-wrap: wrap; }
          .lp-summary { grid-template-columns: 1fr; }
          .lp-toc ol { columns: 1; }
        }
      `}</style>

      <div className="lp-root">
        <div className="lp-page">
          <header className="lp-header">
            <img className="lp-mark" src="/logoIcon.png" alt="Lisual" />
            <div className="lp-head-text">
              <h1>{lang === "es" ? "Política de Privacidad de Lisual" : "Lisual Privacy Policy"}</h1>
              <div className="lp-head-meta">
                <span>{lang === "es" ? "Fecha de vigencia: 9 de septiembre de 2026" : "Effective date: September 9, 2026"}</span>
              </div>
            </div>
            <div className="lp-lang-toggle">
              <button className={lang === "es" ? "active" : ""} onClick={() => setLang("es")}>ES</button>
              <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>EN</button>
            </div>
          </header>

          {lang === "es" ? (
            <>
              <div className="lp-summary">
                <div className="lp-summary-card yes">
                  <p className="lp-eyebrow">Recopilamos</p>
                  <ul>
                    <li>Email de acceso y token de sesión</li>
                    <li>Fotos que capturás o archivos cargados</li>
                    <li>Datos de proyectos y usuarios asociados a tu cuenta</li>
                  </ul>
                </div>
                <div className="lp-summary-card no">
                  <p className="lp-eyebrow">No recopilamos</p>
                  <ul>
                    <li>Micrófono / audio</li>
                    <li>Ubicación</li>
                    <li>Identificadores de publicidad o analítica</li>
                  </ul>
                </div>
                <div className="lp-summary-card yes">
                  <p className="lp-eyebrow">Almacenamiento</p>
                  <ul>
                    <li>Token de sesión: en el dispositivo, cifrado (Android Keystore / iOS Keychain)</li>
                    <li>Fotos y datos de proyectos: proveedores de almacenamiento en la nube, vía HTTPS</li>
                  </ul>
                </div>
                <div className="lp-summary-card yes">
                  <p className="lp-eyebrow">Contacto</p>
                  <ul>
                    <li>jairo@lisual.com</li>
                    <li>Lisual LLC</li>
                  </ul>
                </div>
              </div>

              <nav className="lp-toc">
                <p className="lp-eyebrow">En esta página</p>
                <ol>
                  <li><a href="#who">1. A quién aplica esta política</a></li>
                  <li><a href="#collect">2. Información que recopilamos</a></li>
                  <li><a href="#permissions">3. Permisos del dispositivo</a></li>
                  <li><a href="#use">4. Cómo la usamos</a></li>
                  <li><a href="#share">5. Compartición y divulgación</a></li>
                  <li><a href="#retention">6. Retención y eliminación</a></li>
                  <li><a href="#security">7. Seguridad</a></li>
                  <li><a href="#children">8. Privacidad de menores</a></li>
                  <li><a href="#changes">9. Cambios a esta política</a></li>
                  <li><a href="#social">10. Cuentas de redes sociales conectadas</a></li>
                  <li><a href="#data-deletion">11. Cómo pedir la eliminación de tus datos</a></li>
                  <li><a href="#contact">12. Contacto</a></li>
                </ol>
              </nav>

              <article className="lp-article">
                <section id="who" className="lp-section">
                  <h2><span className="lp-num">01</span> A quién aplica esta política</h2>
                  <p>LisualApp es una app mobile que usan equipos de trabajo para documentar el avance de proyectos mediante cámaras de obra y fotos. Los usuarios son creados y asignados por el administrador de tu organización — la app no ofrece autorregistro público. Esta política aplica a cualquier persona que inicie sesión en la app Lisual</p>
                </section>

                <section id="collect" className="lp-section">
                  <h2><span className="lp-num">02</span> Información que recopilamos</h2>
                  <div className="lp-table-wrap">
                    <table>
                      <thead>
                        <tr><th>Categoría</th><th>Qué incluye</th><th>Origen</th></tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Credenciales de cuenta</td>
                          <td>Email y contraseña, que se intercambian por un token de sesión</td>
                          <td>Vos, al iniciar sesión</td>
                        </tr>
                        <tr>
                          <td>Fotos y archivos</td>
                          <td>Imágenes capturadas con la cámara de la app, y otros archivos (documentos, PDFs, y similares) que selecciones desde tu dispositivo, para documentación de proyecto/obra</td>
                          <td>Vos, al documentar un proyecto</td>
                        </tr>
                        <tr>
                          <td>Datos de proyecto y cliente</td>
                          <td>Nombres de proyecto, registros de clientes y metadatos de archivo que tu organización almacena en Lisual</td>
                          <td>El backend de Lisual de tu organización</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>No recopilamos ubicación, contactos, audio, ni identificadores de publicidad, y la app no tiene SDKs de analítica ni de publicidad.</p>
                </section>

                <section id="permissions" className="lp-section">
                  <h2><span className="lp-num">03</span> Permisos del dispositivo</h2>
                  <ul>
                    <li><strong>Cámara</strong> — para capturar fotos de proyecto directamente desde la app.</li>
                    <li><strong>Galería de fotos</strong> — para subir una foto existente en lugar de tomar una nueva.</li>
                    <li><strong>Archivos del dispositivo</strong> — para adjuntar documentos u otros archivos ya existentes como parte de la documentación del proyecto.</li>
                  </ul>
                  <p>La app no solicita acceso al micrófono y no captura ni sube video: solo permite tomar fotos con la cámara o adjuntar archivos que ya existen en tu dispositivo (fotos, documentos, y similares).</p>
                </section>

                <section id="use" className="lp-section">
                  <h2><span className="lp-num">04</span> Cómo la usamos</h2>
                  <ul>
                    <li>Para autenticarte y mantener tu sesión activa.</li>
                    <li>Para subir, almacenar y mostrar las fotos y registros de proyecto que tu organización le pide a Lisual que gestione.</li>
                    <li>Para operar las funciones principales: paneles de proyecto, configuración de cámaras, timelapse, vistas de antes/después y archivos de fotos.</li>
                    <li>Para generar, a pedido de tu organización, piezas de marketing sobre sus propios proyectos (por ejemplo, contenido para redes sociales, videos o material promocional) a partir de las fotos y datos que suben.</li>
                  </ul>
                  <p>Este procesamiento es un servicio que tu organización contrata para promocionar sus propios proyectos — no es publicidad dirigida a vos ni a terceros. No usamos identificadores de publicidad, no rastreamos tu actividad entre apps o sitios web, y no vendemos datos a redes publicitarias.</p>
                </section>

                <section id="share" className="lp-section">
                  <h2><span className="lp-num">05</span> Compartición y divulgación</h2>
                  <p>No vendemos datos personales. Las fotos y los datos de proyecto son visibles para otros miembros de tu organización que tengan acceso al mismo proyecto, según lo configure tu administrador. Podemos compartir datos con:</p>
                  <ul>
                    <li>Proveedores de infraestructura que alojan el backend y el almacenamiento de la app en nuestro nombre, bajo contratos que limitan su uso a la prestación de ese servicio.</li>
                    <li>Autoridades, únicamente cuando la ley lo requiera.</li>
                  </ul>
                </section>

                <section id="retention" className="lp-section">
                  <h2><span className="lp-num">06</span> Retención y eliminación</h2>
                  <p>Conservamos los datos de cuenta y proyecto mientras el contrato de tu organización con Lisual esté activo, o mientras sea necesario para prestar el servicio.</p>
                  <p>Al recibir tu pedido, eliminamos las fotos y archivos de tu cuenta. Los registros de facturación se conservan por el plazo que exige la normativa contable/impositiva aplicable, independientemente del pedido de borrado.</p>
                  <p>Como las cuentas las provisiona el administrador de tu organización y no un autorregistro, los pedidos de eliminación deben canalizarse a través de:</p>
                  <ul>
                    <li>El administrador de Lisual de tu organización, que puede actualizarlos o eliminarlos directamente, o</li>
                    <li>jairo@lisual.com, que respondemos dentro de los 3 días hábiles.</li>
                  </ul>
                </section>

                <section id="security" className="lp-section">
                  <h2><span className="lp-num">07</span> Seguridad</h2>
                  <p>Los tokens de sesión se guardan en el dispositivo usando el almacenamiento seguro de la plataforma (iOS Keychain / Android Keystore vía <code>expo-secure-store</code>), nunca en texto plano. Los datos en tránsito entre la app y nuestro backend viajan cifrados con HTTPS. Ningún método de almacenamiento o transmisión es perfectamente seguro, y no podemos garantizar seguridad absoluta.</p>
                </section>

                <section id="children" className="lp-section">
                  <h2><span className="lp-num">08</span> Privacidad de menores</h2>
                  <p>Lisual es una herramienta de uso empresarial para documentación de proyectos y obras. No está dirigida a menores, y no recopilamos a sabiendas datos de personas menores de 13 años.</p>
                </section>

                <section id="changes" className="lp-section">
                  <h2><span className="lp-num">09</span> Cambios a esta política</h2>
                  <p>Si hacemos cambios sustanciales a esta política, actualizaremos la fecha de vigencia de arriba y, cuando corresponda, notificaremos al administrador de tu organización.</p>
                </section>

                <section id="social" className="lp-section">
                  <h2><span className="lp-num">10</span> Cuentas de redes sociales conectadas</h2>
                  <p>En LisualPro (pro.lisual.com) podés conectar, si querés, tu Instagram profesional, tus páginas de Facebook o tu perfil de LinkedIn para programar publicaciones con las fotos y timelapses de tu obra. La conexión se hace en la pantalla oficial de cada red: <strong>nunca vemos ni guardamos tu contraseña</strong>.</p>
                  <p><strong>A qué accedemos:</strong> sólo a lo que la red nos permite con tu autorización: el nombre de usuario o de la página, la foto de perfil, el identificador de la cuenta y un permiso (token) para publicar en tu nombre. <strong>No leemos tus mensajes, tus seguidores, tus comentarios ni tus estadísticas.</strong></p>
                  <p><strong>Para qué:</strong> únicamente para publicar el contenido que vos creás y programás en LisualPro, el día y la hora que elegís, y para mostrarte qué cuenta está conectada. Nunca publicamos nada que no hayas programado vos, no usamos estos datos para publicidad y no los vendemos ni compartimos con terceros.</p>
                  <p><strong>Cómo lo guardamos:</strong> el token se guarda cifrado (AES-256) en nuestros servidores, separado del resto de tus datos. Las fotos y videos que programás se copian a nuestro almacenamiento para que la red pueda descargarlos al publicar.</p>
                  <p><strong>Cuánto tiempo:</strong> mientras la cuenta esté conectada. Si la desconectás, o si quitás el acceso desde la red, borramos el token en ese momento.</p>
                </section>

                <section id="data-deletion" className="lp-section">
                  <h2><span className="lp-num">11</span> Cómo pedir la eliminación de tus datos</h2>
                  <p>Podés borrar los datos que recibimos de Instagram, Facebook o LinkedIn de cualquiera de estas formas:</p>
                  <ol>
                    <li><strong>Desde LisualPro:</strong> entrá a Marketing › Redes sociales y tocá el ícono de desconectar en la cuenta. Borramos el token y los datos de esa cuenta al instante, y cancelamos lo que tenías programado para ella.</li>
                    <li><strong>Desde la red:</strong> en Instagram, Configuración › Apps y sitios web; en Facebook, Configuración › Integraciones comerciales; en LinkedIn, Configuración › Privacidad de datos › Servicios permitidos. Quitá el acceso de Lisual. Cuando la red nos avisa, borramos tus datos automáticamente.</li>
                    <li><strong>Por mail:</strong> escribí a <a href="mailto:jairo@lisual.com">jairo@lisual.com</a> con el asunto "Eliminar mis datos" y el nombre de la cuenta. Borramos todos los datos asociados dentro de los 3 días hábiles y te confirmamos por mail.</li>
                  </ol>
                  <p>Para borrar además tu usuario de LisualPro y las fotos de tus cámaras, seguí lo indicado en la sección 6.</p>
                  <p lang="en"><em><strong>English summary (data deletion instructions):</strong> Lisual only receives the account name, profile picture, account ID and a publishing token when you connect Instagram, Facebook or LinkedIn in LisualPro; tokens are stored encrypted and used only to publish the posts you schedule. To delete this data: (1) disconnect the account in LisualPro › Marketing › Social media (deleted immediately); (2) remove Lisual from the network settings (Instagram: Settings › Apps and websites; Facebook: Settings › Business integrations; LinkedIn: Settings › Data privacy › Permitted services) and we delete your data automatically; or (3) email jairo@lisual.com with the subject "Delete my data" and we delete it within 3 business days and confirm by email.</em></p>
                </section>

                <section id="contact" className="lp-section">
                  <h2><span className="lp-num">12</span> Contacto</h2>
                  <p>
                    Lisual LLC<br />
                    A la atención de: Jairo Cwaik<br />
                    jairo@lisual.com
                  </p>
                </section>
              </article>

              <footer className="lp-footer">
                <span>Lisual · app mobile</span>
                <span>v1.0</span>
              </footer>
            </>
          ) : (
            <>
              <div className="lp-summary">
                <div className="lp-summary-card yes">
                  <p className="lp-eyebrow">We collect</p>
                  <ul>
                    <li>Login email &amp; session token</li>
                    <li>Photos you capture or files you upload</li>
                    <li>Project &amp; user data tied to your account</li>
                  </ul>
                </div>
                <div className="lp-summary-card no">
                  <p className="lp-eyebrow">We don't collect</p>
                  <ul>
                    <li>Microphone / audio</li>
                    <li>Location</li>
                    <li>Advertising or analytics identifiers</li>
                  </ul>
                </div>
                <div className="lp-summary-card yes">
                  <p className="lp-eyebrow">Storage</p>
                  <ul>
                    <li>Session token: on-device, encrypted (Android Keystore / iOS Keychain)</li>
                    <li>Photos &amp; project data: cloud storage providers, over HTTPS</li>
                  </ul>
                </div>
                <div className="lp-summary-card yes">
                  <p className="lp-eyebrow">Contact</p>
                  <ul>
                    <li>jairo@lisual.com</li>
                    <li>Lisual LLC</li>
                  </ul>
                </div>
              </div>

              <nav className="lp-toc">
                <p className="lp-eyebrow">On this page</p>
                <ol>
                  <li><a href="#who">1. Who this policy covers</a></li>
                  <li><a href="#collect">2. Information we collect</a></li>
                  <li><a href="#permissions">3. Device permissions</a></li>
                  <li><a href="#use">4. How we use it</a></li>
                  <li><a href="#share">5. Sharing &amp; disclosure</a></li>
                  <li><a href="#retention">6. Retention &amp; deletion</a></li>
                  <li><a href="#security">7. Security</a></li>
                  <li><a href="#children">8. Children's privacy</a></li>
                  <li><a href="#changes">9. Changes to this policy</a></li>
                  <li><a href="#social">10. Connected social media accounts</a></li>
                  <li><a href="#data-deletion">11. Deleting your data</a></li>
                  <li><a href="#contact">12. Contact</a></li>
                </ol>
              </nav>

              <article className="lp-article">
                <section id="who" className="lp-section">
                  <h2><span className="lp-num">01</span> Who this policy covers</h2>
                  <p>LisualApp is a mobile app used by teams to document project progress through site cameras and photos. User accounts are created and assigned by your organization's administrator — the app does not offer public self-registration. This policy applies to anyone who signs in to the Lisual app.</p>
                </section>

                <section id="collect" className="lp-section">
                  <h2><span className="lp-num">02</span> Information we collect</h2>
                  <div className="lp-table-wrap">
                    <table>
                      <thead>
                        <tr><th>Category</th><th>What it includes</th><th>Collected from</th></tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Account credentials</td>
                          <td>Email and password, exchanged for a session token</td>
                          <td>You, at login</td>
                        </tr>
                        <tr>
                          <td>Photos &amp; files</td>
                          <td>Images captured with the in-app camera, and other files (documents, PDFs, and similar) you select from your device, for project/site documentation</td>
                          <td>You, when documenting a project</td>
                        </tr>
                        <tr>
                          <td>Project &amp; customer data</td>
                          <td>Project names, customer records, and archive metadata your organization stores in Lisual</td>
                          <td>Your organization's Lisual backend</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>We do not collect location data, contacts, audio, or advertising identifiers, and the app has no analytics or advertising SDKs.</p>
                </section>

                <section id="permissions" className="lp-section">
                  <h2><span className="lp-num">03</span> Device permissions</h2>
                  <ul>
                    <li><strong>Camera</strong> — to capture project photos directly in the app.</li>
                    <li><strong>Photo library</strong> — to upload an existing photo instead of taking a new one.</li>
                    <li><strong>Device files</strong> — to attach documents or other existing files as part of the project documentation.</li>
                  </ul>
                  <p>The app does not request microphone access and does not capture or upload video: it only lets you take photos with the camera or attach files that already exist on your device (photos, documents, and similar).</p>
                </section>

                <section id="use" className="lp-section">
                  <h2><span className="lp-num">04</span> How we use it</h2>
                  <ul>
                    <li>To authenticate you and keep your session active.</li>
                    <li>To upload, store, and display the photos and project records your organization asks Lisual to manage.</li>
                    <li>To operate core features: project dashboards, camera setup, timelapse and before/after views, and photo archives.</li>
                    <li>To generate, at your organization's request, marketing materials about its own projects (for example, social media content, videos, or promotional assets) from the photos and data you upload.</li>
                  </ul>
                  <p>This processing is a service your organization contracts to promote its own projects — it is not advertising directed at you or at third parties. We do not use advertising identifiers, we do not track your activity across apps or websites, and we do not sell data to ad networks.</p>
                </section>

                <section id="share" className="lp-section">
                  <h2><span className="lp-num">05</span> Sharing &amp; disclosure</h2>
                  <p>We do not sell personal data. Photos and project data are visible to other members of your organization who have access to the same project, as configured by your administrator. We may share data with:</p>
                  <ul>
                    <li>Infrastructure providers who host the app's backend and storage on our behalf, under contracts that limit their use of it to providing that service.</li>
                    <li>Authorities, only where required by law.</li>
                  </ul>
                </section>

                <section id="retention" className="lp-section">
                  <h2><span className="lp-num">06</span> Retention &amp; deletion</h2>
                  <p>We retain account and project data for as long as your organization's contract with Lisual is active, or as needed to provide the service.</p>
                  <p>Once we receive your request, we delete the photos and files in your account. Billing records are retained for the period required by applicable accounting/tax regulations, regardless of a deletion request.</p>
                  <p>Since accounts are provisioned by your organization's administrator rather than self-registered, deletion requests should go through:</p>
                  <ul>
                    <li>Your organization's Lisual administrator, who can update or remove it directly, or</li>
                    <li>jairo@lisual.com, which we respond to within 3 business days.</li>
                  </ul>
                </section>

                <section id="security" className="lp-section">
                  <h2><span className="lp-num">07</span> Security</h2>
                  <p>Session tokens are stored on-device using the platform's secure storage (iOS Keychain / Android Keystore via <code>expo-secure-store</code>), not in plaintext. Data in transit between the app and our backend is encrypted with HTTPS. No method of storage or transmission is perfectly secure, and we cannot guarantee absolute security.</p>
                </section>

                <section id="children" className="lp-section">
                  <h2><span className="lp-num">08</span> Children's privacy</h2>
                  <p>Lisual is a business tool for project and site documentation. It is not directed at children, and we do not knowingly collect data from anyone under 13.</p>
                </section>

                <section id="changes" className="lp-section">
                  <h2><span className="lp-num">09</span> Changes to this policy</h2>
                  <p>If we make material changes to this policy, we'll update the effective date above and, where appropriate, notify your organization's administrator.</p>
                </section>

                <section id="social" className="lp-section">
                  <h2><span className="lp-num">10</span> Connected social media accounts</h2>
                  <p>In LisualPro (pro.lisual.com) you can optionally connect your Instagram professional account, your Facebook Pages or your LinkedIn profile to schedule posts with the photos and timelapses of your construction site. You connect on each network's official screen: <strong>we never see or store your password</strong>.</p>
                  <p><strong>What we access:</strong> only what the network grants with your authorization: the account or Page name, profile picture, account ID and a token to publish on your behalf. <strong>We do not read your messages, followers, comments or insights.</strong></p>
                  <p><strong>Why:</strong> only to publish the content you create and schedule in LisualPro, at the date and time you choose, and to show you which account is connected. We never publish anything you did not schedule, we do not use this data for advertising, and we do not sell or share it with third parties.</p>
                  <p><strong>How we store it:</strong> the token is stored encrypted (AES-256) on our servers, separately from your other data. Photos and videos you schedule are copied to our storage so the network can download them when publishing.</p>
                  <p><strong>For how long:</strong> while the account stays connected. If you disconnect it, or remove our access from the network, we delete the token at that moment.</p>
                </section>

                <section id="data-deletion" className="lp-section">
                  <h2><span className="lp-num">11</span> How to request deletion of your data</h2>
                  <p>You can delete the data we receive from Instagram, Facebook or LinkedIn in any of these ways:</p>
                  <ol>
                    <li><strong>In LisualPro:</strong> go to Marketing › Social media and click the disconnect icon on the account. We delete the token and that account's data immediately and cancel anything scheduled for it.</li>
                    <li><strong>From the network:</strong> Instagram: Settings › Apps and websites; Facebook: Settings › Business integrations; LinkedIn: Settings › Data privacy › Permitted services. Remove Lisual's access. When the network notifies us, we delete your data automatically.</li>
                    <li><strong>By email:</strong> write to <a href="mailto:jairo@lisual.com">jairo@lisual.com</a> with the subject "Delete my data" and the account name. We delete all associated data within 3 business days and confirm by email.</li>
                  </ol>
                  <p>To also delete your LisualPro user and your camera photos, see section 6.</p>
                </section>

                <section id="contact" className="lp-section">
                  <h2><span className="lp-num">12</span> Contact</h2>
                  <p>
                    Lisual LLC<br />
                    Attn: Jairo Cwaik<br />
                    jairo@lisual.com
                  </p>
                </section>
              </article>

              <footer className="lp-footer">
                <span>Lisual · mobile app</span>
                <span>v1.0</span>
              </footer>
            </>
          )}
        </div>
      </div>
    </>
  );
}
